import moment from 'moment';
import { Button, ButtonStyles, Icons, StatusType, Table, TextWithStatusIndicatorCell } from '@silverfort/sf-components';
import { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
    SECURITY_EVENTS_TYPES,
    PLATFORM_EVENTS_TYPES,
    CLOUD_EVENTS_TYPES,
    AUTHENTICATION_EVENTS_TYPES
} from '@silverfort-nodelibs/common/utils/consts';
import _ from 'lodash';

const ALL_EVENT_TYPES = {
    ...SECURITY_EVENTS_TYPES,
    ...PLATFORM_EVENTS_TYPES,
    ...CLOUD_EVENTS_TYPES,
    ...AUTHENTICATION_EVENTS_TYPES
};

const TABLE_ROW_HEIGHT = 48;
const DEACTIVATION_DISABLED_REASON = 'This integration is currently in use and cannot be disabled or deleted.';

const AlertsManagementSubTable = ({
    isEditor,
    providerName,
    integrations,
    lastModifiedKey = 'lastModified',
    handleEditIntegration,
    handleDeleteIntegration,
    handleToggleIntegration
}) => {
    const toggledIds = useMemo(
        () => integrations.filter((integration) => integration.enabled).map((integration) => integration.id),
        [integrations]
    );

    const [integrationNameToRemove, setIntegrationNameToRemove] = useState<string>('');

    const [sortBy, setSortBy] = useState<{ columnKey: string; direction: 'asc' | 'desc' }>({
        columnKey: 'name',
        direction: 'asc'
    });

    const getEventsDataForIntegration = (integration) => {
        const activeEvents = integration.activeEvents ?? {};

        return Object.entries(activeEvents)
            .filter(([eventId, isActive]) => isActive)
            .map(([eventId]) => {
                const eventLabel = Object.values(ALL_EVENT_TYPES).find((event) => event.id === eventId)?.label ?? '';
                return eventLabel;
            })
            .sort();
    };

    const sortedRows = useMemo(() => {
        const buildRows = () =>
            integrations.map((integration) => ({
                id: integration.id,
                name: integration.name,
                status: integration.custom?.testPassed,
                eventsData: getEventsDataForIntegration(integration),
                lastUpdated: integration[lastModifiedKey] && moment(integration[lastModifiedKey]).local().fromNow(),
                creationDate:
                    integration.creationDate && moment(integration.creationDate).local().format('MMM DD, YYYY'),
                custom: integration.custom
            }));

        return _.orderBy(buildRows(), [sortBy.columnKey, 'name'], [sortBy.direction, 'asc']);
    }, [integrations, lastModifiedKey, sortBy]);

    const onToggledIntegration = (newToggledIds) => {
        const added = newToggledIds.find((integrationId) => !toggledIds.includes(integrationId));
        const removed = toggledIds.find((integrationId) => !newToggledIds.includes(integrationId));
        handleToggleIntegration(added || removed);
    };

    return (
        <>
            <div className="w-[calc(100vw-400px)] h-full">
                <Table
                    toggleable={true}
                    headerToggleable={false}
                    getRowToggleProps={(row) => {
                        if (!isEditor) {
                            return { disabled: true };
                        }

                        return row && row.custom?.deactivationDisabled
                            ? { disabled: true, tooltipTitle: DEACTIVATION_DISABLED_REASON }
                            : { disabled: false, tooltipTitle: '' };
                    }}
                    isHorizontalScrollAvailable={true}
                    keyExtractor={(integration) => integration.id}
                    selected={toggledIds}
                    rowSelectedClassName={''}
                    onSortChange={(columnKey, direction) => setSortBy({ columnKey: columnKey as string, direction })}
                    bodyHeight={integrations.length * TABLE_ROW_HEIGHT}
                    onSelect={onToggledIntegration}
                    columns={[
                        { columnKey: 'name', title: 'Name' },
                        {
                            columnKey: 'status',
                            title: 'Status',
                            render: ({ status }) => {
                                if (status === undefined) {
                                    return null;
                                }

                                const { text, statusType } = status
                                    ? { text: 'Active', statusType: StatusType.Default }
                                    : { text: 'Disconnected', statusType: StatusType.Disabled };

                                return <TextWithStatusIndicatorCell text={text} statusType={statusType} />;
                            }
                        },
                        {
                            columnKey: 'eventsData',
                            title: 'Events Data',
                            columnClassName: 'min-w-[350px]',
                            render: ({ eventsData }) => (
                                <div className="flex gap-1 w-full min-w-0">
                                    {eventsData.slice(0, 2).map((email) => (
                                        <div
                                            key={email}
                                            className="flex items-center rounded border px-1 py-0.5 font-sf-primary text-xs text-sf-gray-800 border-sf-gray-100 select-none max-w-[30vw] min-w-[40px]"
                                            title={email}>
                                            <div className="leading-tight truncate">{email}</div>
                                        </div>
                                    ))}
                                    {eventsData.length > 2 && (
                                        <div className="flex h-6 items-center rounded border px-2 font-sf-primary text-12 text-sf-gray-800 select-none">{`+${
                                            eventsData.length - 2
                                        }`}</div>
                                    )}
                                </div>
                            )
                        },
                        { columnKey: 'lastUpdated', title: 'Last Updated' },
                        { columnKey: 'creationDate', title: 'Creation Date' }
                    ]}
                    rows={sortedRows}
                    getRowMenuItems={(row) => [
                        {
                            IconLeft: Icons.Edit,
                            title: 'Edit',
                            onClick: (integration) => handleEditIntegration(integration.id),
                            disabled: !isEditor
                        },
                        {
                            IconLeft: Icons.Delete,
                            title: 'Delete',
                            onClick: (integration) => setIntegrationNameToRemove(integration.id),
                            disabled: !isEditor || row.custom?.deactivationDisabled,
                            tooltipDescription: row.custom?.deactivationDisabled ? DEACTIVATION_DISABLED_REASON : ''
                        }
                    ]}
                    menuItems={[
                        {
                            IconLeft: Icons.Edit,
                            title: 'Edit',
                            onClick: (integration) => handleEditIntegration(integration.id),
                            disabled: !isEditor
                        },
                        {
                            IconLeft: Icons.Delete,
                            title: 'Delete',
                            onClick: (integration) => setIntegrationNameToRemove(integration.id),
                            disabled: !isEditor
                        }
                    ]}></Table>
            </div>
            {integrationNameToRemove && (
                <Modal
                    className="flex flex-col justify-center"
                    id={'cancelModal'}
                    dialogClassName="w-[360px] h-50 [&_.modal-content]:rounded-lg [&_.modal-content]:shadow-modal-light"
                    show
                    onHide={() => setIntegrationNameToRemove('')}
                    keyboard={false}
                    backdrop="static">
                    <Modal.Header className="border-hidden pt-4 pr-6 pb-0 pl-0" closeButton>
                        <div className="font-sf px-4 pb-4 mb-1 text-14 font-bold text-sf-gray-800">
                            Remove Integration
                        </div>
                    </Modal.Header>
                    <Modal.Body className="font-sf text-13 px-4 pb-6 h-full border-t border-b">
                        Are you sure you want to remove the “{providerName}” integration? This action will stop all data
                        collection and will delete all related data.
                    </Modal.Body>
                    <Modal.Footer className="border-hidden px-6">
                        <div className="flex justify-end items-center">
                            <Button
                                onClick={() => setIntegrationNameToRemove('')}
                                text={'No, Keep it'}
                                buttonStyle={ButtonStyles.TertiarySolid}
                                className="mr-2"
                            />
                            <Button
                                onClick={() => {
                                    setIntegrationNameToRemove('');
                                    handleDeleteIntegration(integrationNameToRemove);
                                }}
                                buttonStyle={ButtonStyles.DangerSolid}
                                text={'Yes, Delete'}
                            />
                        </div>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
};

export default AlertsManagementSubTable;
